import Vue from 'vue';
import { ApolloClient } from 'apollo-client';
import VueApollo from 'vue-apollo';
import { ApolloLink, concat, Observable } from 'apollo-link';
import { InMemoryCache, IntrospectionFragmentMatcher } from 'apollo-cache-inmemory';
import ActionCable from 'actioncable';
import ActionCableLink from 'graphql-ruby-client/subscriptions/ActionCableLink';
import { getMainDefinition } from 'apollo-utilities';
import { AUTH_TOKEN, HTTP_ENDPOINT, WS_ENDPOINT, WS_ENDPOINT2, JWT_HMAC_SECRET, AES_SECRET_KEY, } from '@/helpers/constants/app';
import jsonwebtoken from 'jsonwebtoken';
import moment from 'moment';
import CryptoJS from 'crypto-js';
/**
 * Apollo Client Uploader
 * @see https://github.com/jaydenseric/apollo-upload-client
 */
import { createUploadLink } from 'apollo-upload-client';
// import { cookie } from '@/helpers/Cookie';
/**
 * Initialize Fragment Matcher using Fetched Fragment Types in
 * `./fragmentTypes.json`
 * This will allow client to validate and match interfaces and fragments
 * https://www.apollographql.com/docs/react/advanced/fragments.html#fragment-matcher
 *
 * Surprisingly This also works by passing an empty json file which
 *  contain fragment type(seems like a bug?)
 *  see: https://github.com/apollographql/apollo-client/issues/3397
 */
const fragmentMatcher = new IntrospectionFragmentMatcher({
    introspectionQueryResultData: {
        __schema: {
            types: [],
        },
    },
});
function encrypt(payload) {
    const iv = CryptoJS.lib.WordArray.random(16);
    const salt = CryptoJS.lib.WordArray.random(16);
    const key = CryptoJS.PBKDF2(AES_SECRET_KEY, salt, {
        keySize: 256 / 32,
        iterations: 100,
    });
    const cipher = CryptoJS.AES.encrypt(payload, key, {
        iv,
    });
    const cipheredToken = CryptoJS.enc.Hex.parse(salt.toString() + iv.toString() + cipher.ciphertext.toString()).toString(CryptoJS.enc.Base64);
    const decodeCiphered = CryptoJS.enc.Utf8.parse(cipheredToken);
    return CryptoJS.enc.Base64.stringify(decodeCiphered);
}
const timestamp = moment().unix();
// JWT Encoder
// https://www.npmjs.com/package/jsonwebtoken
const defaultToken = jsonwebtoken.sign({ code: timestamp }, `${JWT_HMAC_SECRET}`);
const base64Token = encrypt(defaultToken);
Vue.use(VueApollo);
const primaryCable = ActionCable.createConsumer(WS_ENDPOINT);
// for inbox message subscriptions
const secondaryCable = ActionCable.createConsumer(WS_ENDPOINT2);
const httpLink = createUploadLink({
    uri: HTTP_ENDPOINT,
});
// add the authorization to the headers
// https://github.com/Akryum/vue-apollo/issues/144
function middleware(token) {
    return new ApolloLink((operation, forward) => {
        operation.setContext({
            headers: {
                authorization: `Bearer ${token === '' ? base64Token : token}`,
            },
        });
        // issue for argument `forward` cannot invoke an object
        // which is possibly 'undefined'
        // https://github.com/apollographql/apollo-link/issues/362
        if (forward) {
            return forward(operation);
        }
        return null;
    });
}
// const storedToken = cookie(AUTH_TOKEN) || '';
const storedToken = sessionStorage.getItem(AUTH_TOKEN) || '';
// Reason why we dont use createApolloClient builder
// it doesn't support the custom websocket link
// https://github.com/Akryum/vue-cli-plugin-apollo/blob/master/graphql-client/src/index.js#L111
const primaryWsLink = new ActionCableLink({
    cable: primaryCable,
    connectionParams: {
        token: `Bearer ${storedToken === '' ? base64Token : storedToken}`,
    },
});
const secondaryWsLink = new ActionCableLink({
    cable: secondaryCable,
    connectionParams: {
        token: `Bearer ${storedToken === '' ? base64Token : storedToken}`,
    },
});
const link = ApolloLink.split(({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
}, new ApolloLink((operation) => {
    return new Observable((observer) => {
        const subscription1 = primaryWsLink.request(operation)?.subscribe({
            next: (result) => observer.next(result),
            error: (error) => console.error('WS error:', error),
        });
        const subscription2 = secondaryWsLink.request(operation)?.subscribe({
            next: (result) => observer.next(result),
            error: (error) => console.error('WS error:', error),
        });
        return () => {
            if (subscription1)
                subscription1.unsubscribe();
            if (subscription2)
                subscription2.unsubscribe();
        };
    });
}), httpLink);
let defaultClient;
function client(token) {
    const authMiddleware = middleware(token);
    defaultClient = new ApolloClient({
        link: concat(authMiddleware, link),
        cache: new InMemoryCache({
            fragmentMatcher,
            // Normalize Cached Objects by assigning `uuid` as identifier.
            // This is applicable for `readFragment` which allows us to query
            //  objects in store independently w/out executing `readStore`.
            // https://www.apollographql.com/docs/react/advanced/caching.html
            dataIdFromObject: (object) => object.uuid || null,
        }),
    });
    return defaultClient;
}
/**
 * Function for Initializing Apollo Provider
 * To reactively update Authorization Token
 * we pass token as argument upon `Login`.
 * The rest of request tokens are from cookie.
 */
export function createProvider(token = storedToken) {
    const apolloProvider = new VueApollo({
        defaultClient: client(token),
    });
    return apolloProvider;
}
/**
 * Asynchronous Function to reset Apollo Store
 * This function initialize Apollo Provider
 * and call `resetStore()` function.
 */
export async function resetStore() {
    try {
        await defaultClient.clearStore();
    }
    catch (e) {
        /* eslint-disable */
        // tslint:disable-next-line
        console.log('%cError on cache reset (${event})', 'color: orange;', e.message);
        // @ts-ignore
        defaultClient.cache.data.clear();
        /* eslint-enable */
    }
}
